import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { AttributeGroup } from '../../types';
import BulkUpdateAttributeRow from './BulkUpdateAttributeRow';
import {
  type BulkAttributeConfig,
  type BulkAttributeState,
  type BulkAttributeValue,
} from './BulkUpdateInputRenderer';
import styles from './BulkUpdateDialog.module.css';

type BulkUpdateAttributeGroupSectionProps = {
  group: AttributeGroup;
  groupId: number;
  items: BulkAttributeConfig[];
  isCollapsed: boolean;
  language: string;
  attributesState: Record<string, BulkAttributeState>;
  getInitialValueByType: (attribute: BulkAttributeConfig) => BulkAttributeValue;
  onGroupCollapseToggle: (groupId: number) => void;
  onAttributeCheckboxToggle: (attribute: BulkAttributeConfig, checked: boolean) => void;
  onValueChange: (attribute: BulkAttributeConfig, value: BulkAttributeValue) => void;
};

function getLocalizedGroupLabel(group: AttributeGroup, language: string) {
  return language === 'de' ? group.attributeGroupNamede : group.attributeGroupNameen;
}

export default function BulkUpdateAttributeGroupSection({
  group,
  groupId,
  items,
  isCollapsed,
  language,
  attributesState,
  getInitialValueByType,
  onGroupCollapseToggle,
  onAttributeCheckboxToggle,
  onValueChange,
}: BulkUpdateAttributeGroupSectionProps) {
  const groupLabel = getLocalizedGroupLabel(group, language);

  return (
    <section key={groupId} className={styles.groupSection}>
      <button
        type='button'
        className={styles.groupToggleButton}
        onClick={() => onGroupCollapseToggle(groupId)}
        aria-expanded={!isCollapsed}
      >
        <span className={styles.groupLabel}>
          <span className={styles.groupArrowIcon} aria-hidden='true'>
            {isCollapsed ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </span>
          {groupLabel}
        </span>
      </button>

      {!isCollapsed && (
        <div className={styles.groupBody}>
          {items.length === 0 ? (
            <div className={styles.groupEmptyState}>No attributes found in this group.</div>
          ) : (
            items.map((attribute) => {
              const state = attributesState[attribute.attrInternalName] ?? {
                enabled: false,
                value: getInitialValueByType(attribute),
                error: false,
              };

              return (
                <BulkUpdateAttributeRow
                  key={attribute.attrInternalName}
                  attribute={attribute}
                  state={state}
                  language={language}
                  onAttributeCheckboxToggle={onAttributeCheckboxToggle}
                  onValueChange={onValueChange}
                />
              );
            })
          )}
        </div>
      )}
    </section>
  );
}
